import { getAuthHeaders } from "../lib/auth";
import { useState, useEffect, useCallback } from "react";
import CourierLayout from "../components/CourierLayout";
import { useAuth } from "../App";
import {
  Package, Truck, Clock, CheckCircle2, AlertTriangle,
  MapPin, ArrowRight, RefreshCw, UserPlus, Inbox, Navigation
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const getHeaders = getAuthHeaders;

const STATUS_CONFIG = {
  assigned: { label: "Assignée", color: "bg-blue-100 text-blue-700 border-blue-200" },
  picked_up: { label: "Recuperee", color: "bg-cyan-100 text-cyan-700 border-cyan-200" },
  in_transit: { label: "En transit", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  delivered: { label: "Livree", color: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  completed: { label: "Terminée", color: "bg-green-100 text-green-700 border-green-200" },
  failed: { label: "Echouee", color: "bg-red-100 text-red-700 border-red-200" },
  created: { label: "Disponible", color: "bg-amber-100 text-amber-700 border-amber-200" },
};

const NEXT_STATUS = { assigned: "picked_up", picked_up: "in_transit", in_transit: "delivered", delivered: "completed" };
const NEXT_LABEL = { assigned: "Recuperer", picked_up: "En transit", in_transit: "Livrer", delivered: "Terminer" };

export default function CourierDashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [myCourses, setMyCourses] = useState([]);
  const [poolCourses, setPoolCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [gpsActive, setGpsActive] = useState(false);
  const [gpsWatchId, setGpsWatchId] = useState(null);

  // GPS tracking
  const startGPS = () => {
    if (!navigator.geolocation) { toast.error("Geolocalisation non supportee"); return; }
    const watchId = navigator.geolocation.watchPosition(
      async (pos) => {
        try {
          await axios.post(`${API}/log/courses/gps/update`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy: pos.coords.accuracy,
            heading: pos.coords.heading,
            speed: pos.coords.speed,
          }, { headers: getHeaders() });
        } catch (e) { /* silent */ }
      },
      (err) => { toast.error("Erreur GPS: " + err.message); },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );
    setGpsWatchId(watchId);
    setGpsActive(true);
    toast.success("Suivi GPS active");
  };

  const stopGPS = () => {
    if (gpsWatchId !== null) navigator.geolocation.clearWatch(gpsWatchId);
    setGpsWatchId(null);
    setGpsActive(false);
    toast.info("Suivi GPS desactive");
  };

  const load = useCallback(async () => {
    try {
      const h = getHeaders();
      const [coursesRes, statsRes] = await Promise.all([
        axios.get(`${API}/log/courses`, { headers: h }),
        axios.get(`${API}/log/courses/stats`, { headers: h }),
      ]);
      const all = coursesRes.data.courses || [];
      const myId = user?.id;
      setMyCourses(all.filter(c => c.courier_id === myId && !["completed", "failed"].includes(c.status)));
      setPoolCourses(all.filter(c => (!c.courier_id || c.courier_id === "") && !["completed", "failed"].includes(c.status)));
      setStats(statsRes.data);
    } catch { }
    finally { setLoading(false); }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const advanceStatus = async (courseId, currentStatus) => {
    const nextStatus = NEXT_STATUS[currentStatus];
    if (!nextStatus) return;
    setUpdatingId(courseId);
    try {
      await axios.put(`${API}/log/courses/${courseId}/status`, { status: nextStatus, note: "" }, { headers: getHeaders() });
      toast.success(`Statut: ${STATUS_CONFIG[nextStatus]?.label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setUpdatingId(null); }
  };

  const markFailed = async (courseId) => {
    setUpdatingId(courseId);
    try {
      await axios.put(`${API}/log/courses/${courseId}/status`, { status: "failed", note: "Echec signale par le agent commercial" }, { headers: getHeaders() });
      toast.success("Expedition marquee comme echouee");
      load();
    } catch (e) { toast.error("Erreur"); }
    finally { setUpdatingId(null); }
  };

  const selfAssign = async (courseId) => {
    setUpdatingId(courseId);
    try {
      await axios.put(`${API}/log/courses/${courseId}/self-assign`, {}, { headers: getHeaders() });
      toast.success("Expedition prise en charge !");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setUpdatingId(null); }
  };

  return (
    <CourierLayout title={`Bonjour, ${user?.full_name?.split(" ")[0] || "Agent Commercial"}`} subtitle="Votre tableau de bord agent commercial">
      <div data-testid="courier-dashboard" className="space-y-6">
        {/* GPS Tracking Button */}
        <div className={`flex items-center gap-3 p-3 rounded-xl border ${gpsActive ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 shadow-sm"}`} data-testid="gps-control">
          <Navigation className={`w-5 h-5 ${gpsActive ? "text-emerald-500 animate-pulse" : "text-slate-400"}`} />
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-800">Partage de position GPS</p>
            <p className="text-[10px] text-slate-500">{gpsActive ? "Position partagee en temps reel" : "Activez pour partager votre position"}</p>
          </div>
          <Button onClick={gpsActive ? stopGPS : startGPS} size="sm" data-testid="gps-toggle-btn"
            className={gpsActive ? "bg-red-100 text-red-600 hover:bg-red-200 border border-red-200" : "bg-emerald-500 hover:bg-emerald-400 text-white"}>
            {gpsActive ? "Desactiver" : "Activer GPS"}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Mes expeditions" value={myCourses.length} icon={Package} color="emerald" testId="stat-my-courses" />
          <StatCard label="Disponibles" value={poolCourses.length} icon={Inbox} color="amber" testId="stat-pool" />
          <StatCard label="Taux succès" value={`${stats?.success_rate || 0}%`} icon={CheckCircle2} color="green" testId="stat-success" />
          <StatCard label="Total completees" value={(stats?.by_status?.completed || 0) + (stats?.by_status?.delivered || 0)} icon={Truck} color="blue" testId="stat-completed" />
        </div>

        {/* Pool: Available courses */}
        {poolCourses.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Inbox className="w-4 h-4 text-amber-400" />
              <h2 className="text-base font-semibold text-amber-600">Expéditions disponibles ({poolCourses.length})</h2>
            </div>
            <div className="space-y-2">
              {poolCourses.map(course => (
                <PoolCourseCard key={course.id} course={course} onSelfAssign={selfAssign} isUpdating={updatingId === course.id} />
              ))}
            </div>
          </div>
        )}

        {/* My courses */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-slate-900">Mes expeditions actives ({myCourses.length})</h2>
            <Button variant="ghost" size="sm" onClick={() => { setLoading(true); load(); }} className="text-slate-500 hover:text-slate-800 gap-1.5" data-testid="refresh-courses">
              <RefreshCw className="w-3.5 h-3.5" /> Rafraichir
            </Button>
          </div>
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500 border-t-transparent" /></div>
          ) : myCourses.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200 shadow-sm">
              <CheckCircle2 className="w-10 h-10 text-emerald-500/40 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Aucune course en cours</p>
              <p className="text-slate-400 text-xs mt-1">{poolCourses.length > 0 ? "Prenez en charge une course disponible ci-dessus" : "De nouvelles courses apparaitront ici"}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {myCourses.map(course => (
                <MyCourseCard key={course.id} course={course} onAdvance={advanceStatus} onFail={markFailed} isUpdating={updatingId === course.id} />
              ))}
            </div>
          )}
        </div>
      </div>
    </CourierLayout>
  );
}

function StatCard({ label, value, icon: Icon, color, testId }) {
  const colors = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    green: "bg-green-50 text-green-700 border-green-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
  };
  const iconColors = { emerald: "text-emerald-500", amber: "text-amber-500", green: "text-green-500", blue: "text-blue-500" };
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${colors[color]}`} data-testid={testId}>
      <Icon className={`w-5 h-5 mb-2 ${iconColors[color]}`} />
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-[10px] text-slate-500 uppercase mt-1">{label}</p>
    </div>
  );
}

function PoolCourseCard({ course, onSelfAssign, isUpdating }) {
  const priorityColor = course.priority === "critique" ? "text-red-600" : course.priority === "urgent" ? "text-amber-600" : "text-slate-500";
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 hover:border-amber-300 transition-colors shadow-sm" data-testid={`pool-course-${course.id}`}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${course.course_type === "assurance" ? "bg-blue-100" : "bg-violet-100"}`}>
              {course.course_type === "assurance" ? <Truck className="w-4 h-4 text-blue-600" /> : <Package className="w-4 h-4 text-violet-600" />}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{course.title}</p>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[10px] font-medium ${priorityColor}`}>[{course.priority}]</span>
                <span className="text-[10px] text-slate-500">{course.course_type === "assurance" ? "Assurance" : "Générale"}</span>
                <span className="text-[10px] text-slate-500">par {course.created_by_name}</span>
              </div>
            </div>
          </div>
          <span className="px-2.5 py-1 rounded-full border text-[11px] font-medium bg-amber-100 text-amber-700 border-amber-300">Disponible</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600 bg-white rounded-lg p-2.5 border border-slate-100">
          <MapPin className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
          <span className="truncate">{course.pickup_address}</span>
          <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
          <MapPin className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <span className="truncate">{course.delivery_address}</span>
        </div>
        <Button size="sm" disabled={isUpdating} onClick={() => onSelfAssign(course.id)}
          className="bg-amber-600 hover:bg-amber-500 text-white text-xs gap-1.5 w-full sm:w-auto" data-testid={`self-assign-${course.id}`}>
          {isUpdating ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <><UserPlus className="w-3.5 h-3.5" /> Prendre en charge</>}
        </Button>
      </div>
    </div>
  );
}

function MyCourseCard({ course, onAdvance, onFail, isUpdating }) {
  const config = STATUS_CONFIG[course.status] || STATUS_CONFIG.created;
  const nextLabel = NEXT_LABEL[course.status];
  const priorityColor = course.priority === "critique" ? "text-red-600" : course.priority === "urgent" ? "text-amber-600" : "text-slate-500";
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 hover:border-emerald-300 transition-colors shadow-sm" data-testid={`my-course-${course.id}`}>
      <div className="flex flex-col gap-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${course.course_type === "assurance" ? "bg-blue-100" : "bg-violet-100"}`}>
              {course.course_type === "assurance" ? <Truck className="w-4 h-4 text-blue-600" /> : <Package className="w-4 h-4 text-violet-600" />}
            </div>
            <div>
              <p className="text-sm font-medium text-slate-900">{course.title}</p>
              <span className={`text-[10px] font-medium ${priorityColor}`}>[{course.priority}]</span>
            </div>
          </div>
          <span className={`px-2.5 py-1 rounded-full border text-[11px] font-medium ${config.color}`}>{config.label}</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 rounded-lg p-2.5 border border-slate-100">
          <MapPin className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
          <span className="truncate">{course.pickup_address}</span>
          <ArrowRight className="w-3 h-3 text-slate-400 flex-shrink-0" />
          <MapPin className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          <span className="truncate">{course.delivery_address}</span>
        </div>
        {(course.pickup_contact || course.delivery_contact) && (
          <div className="flex gap-4 text-[10px] text-slate-500">
            {course.pickup_contact && <span>Recup: {course.pickup_contact} {course.pickup_phone}</span>}
            {course.delivery_contact && <span>Livr: {course.delivery_contact} {course.delivery_phone}</span>}
          </div>
        )}
        <div className="flex items-center gap-2 pt-1">
          {nextLabel && (
            <Button size="sm" disabled={isUpdating} onClick={() => onAdvance(course.id, course.status)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs gap-1.5 flex-1 sm:flex-none" data-testid={`advance-course-${course.id}`}>
              {isUpdating ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <><ArrowRight className="w-3.5 h-3.5" />{nextLabel}</>}
            </Button>
          )}
          {!["completed", "failed", "delivered"].includes(course.status) && (
            <Button size="sm" variant="outline" disabled={isUpdating} onClick={() => onFail(course.id)}
              className="border-red-200 text-red-600 hover:bg-red-50 text-xs" data-testid={`fail-course-${course.id}`}>Echec</Button>
          )}
          <span className="text-[10px] text-slate-500 ml-auto">{new Date(course.created_at).toLocaleDateString("fr-FR")}</span>
        </div>
      </div>
    </div>
  );
}
